import { cn } from '@exam-room/utils';
import { useDisclosure } from '@shared/hooks';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  CalculateOutlinedIcon,
  CloseRoundedIcon,
  DesignServicesOutlinedIcon,
  FactCheckOutlinedIcon,
  IconButton,
  InsertDriveFileOutlinedIcon,
} from '../../../components';
import { ExamOverview } from '../../../containers';
import { useExamEvent } from '../../../providers';
import { type QuestionWithOrder } from '../../../providers/new-exam/types';
import { getQuestionOrder } from '../../../utils';
import { Calculator, DocumentModal } from '../components';

interface ExamToolsProps {
  show: boolean;
  onClose: () => void;
  currentExamOrder: number | null;
  questions: QuestionWithOrder[];
  url?: string | null;
}

export default function ExamTools(props: Readonly<ExamToolsProps>) {
  const { show, onClose, questions, currentExamOrder, url } = props;
  const [showCalculator, setShowCalculator] = useState(false);
  const [isDocumentModalOpen, { toggle }] = useDisclosure(false);

  const { t } = useTranslation();
  const { examEvent } = useExamEvent();

  const { minQuestionOrder, maxQuestionOrder } = getQuestionOrder(questions);

  const shouldShowSectionText = examEvent.metadata.events.length > 1;
  const currentExamSection =
    shouldShowSectionText && currentExamOrder
      ? `${t('examReviewHeader.section')} ${currentExamOrder}`
      : undefined;
  const header = [t('examReviewHeader.answerSheet'), currentExamSection].filter(Boolean).join(' ');

  return (
    <div
      className={cn(
        'no-scrollbar flex h-full w-[362px] flex-col overflow-y-scroll rounded-2xl bg-white shadow-sm transition-all select-none',
        show ? 'translate-x-0 opacity-100' : 'w-0 translate-x-full opacity-0',
      )}
    >
      <div className='text-text-primary flex flex-1 flex-col gap-4 p-4'>
        <div className='flex justify-between gap-4'>
          <div className='flex items-center gap-2 py-1.5 whitespace-nowrap'>
            <DesignServicesOutlinedIcon />
            <h5>{t('header.examTools')}</h5>
          </div>
          <IconButton onClick={onClose} className='p-2'>
            <CloseRoundedIcon />
          </IconButton>
        </div>
        <hr className='border-border-divider' />

        <div className='flex items-center gap-2'>
          <FactCheckOutlinedIcon className='text-xl' />
          <div className='text-body1'>
            <span className='!font-semibold'>{header}:</span>
            {` ${t('question.item', {
              number: minQuestionOrder,
            })} - ${maxQuestionOrder}`}
          </div>
        </div>
        <div className='w-[330px]'>
          <ExamOverview minified />
        </div>
        {url ? (
          <>
            <hr className='border-border-divider' />
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-2'>
                <InsertDriveFileOutlinedIcon className='text-xl' />
                <div className='text-body1 !font-semibold'>{t('examTools.referenceDocs')}</div>
              </div>
              <Button
                variant='outlined'
                size='sm'
                className='text-body2 w-20 !font-semibold'
                onClick={toggle}
              >
                {t('examTools.open')}
              </Button>
              <DocumentModal open={isDocumentModalOpen} onClose={toggle} url={url} />
            </div>
          </>
        ) : null}
        <hr className='border-border-divider' />
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <CalculateOutlinedIcon className='text-xl' />
            <div className='text-body1 !font-semibold'>{t('examTools.calculator')}</div>
          </div>
          <Button
            variant='outlined'
            size='sm'
            className='text-body2 w-20 !font-semibold'
            onClick={() => setShowCalculator((v) => !v)}
          >
            {showCalculator ? t('examTools.hide') : t('examTools.show')}
          </Button>
        </div>
        <div
          className={cn(
            'origin-top transition-all duration-300',
            showCalculator ? 'h-auto scale-y-100 opacity-100' : 'h-0 scale-y-0 opacity-0',
          )}
        >
          <Calculator />
        </div>
      </div>
    </div>
  );
}
